'use client';

import { useCallback, useEffect, useState } from 'react';

/**
 * Theme state: 'light' | 'dark' | 'system'.
 *
 * The applied class is stamped on <html> by the inline script in layout.tsx
 * *before* first paint — doing it in an effect instead would show a flash of
 * the wrong theme on every page load. This module keeps React in sync with
 * whatever that script decided, and owns changes from then on.
 */

export type Theme = 'light' | 'dark' | 'system';
export type Resolved = 'light' | 'dark';

export const THEME_KEY = 'gt_theme';

/** The script that runs before paint. Kept here so it cannot drift from the hook. */
export const THEME_INIT_SCRIPT = `
(function(){
  try {
    var t = localStorage.getItem('${THEME_KEY}') || 'system';
    var dark = t === 'dark' || (t === 'system' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);
    document.documentElement.classList.toggle('dark', dark);
    document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
  } catch (e) {}
})();
`;

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined'
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function apply(theme: Theme): Resolved {
  const dark = theme === 'dark' || (theme === 'system' && systemPrefersDark());
  document.documentElement.classList.toggle('dark', dark);
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
  return dark ? 'dark' : 'light';
}

/**
 * Subscribers are tracked module-side so that several components (the header
 * toggle, the charts) all re-render on a theme change without a provider
 * wrapping the tree.
 */
const listeners = new Set<() => void>();
function broadcast() {
  listeners.forEach((fn) => fn());
}

export function useTheme() {
  // Start at 'system' on both server and first client render so the markup
  // matches; the real value is read in the effect below. The pre-paint script
  // has already applied the correct class, so nothing visibly changes.
  const [theme, setThemeState] = useState<Theme>('system');
  const [resolved, setResolved] = useState<Resolved>('light');

  useEffect(() => {
    const stored = (localStorage.getItem(THEME_KEY) as Theme | null) ?? 'system';
    setThemeState(stored);
    setResolved(document.documentElement.classList.contains('dark') ? 'dark' : 'light');

    const sync = () => {
      const cur = (localStorage.getItem(THEME_KEY) as Theme | null) ?? 'system';
      setThemeState(cur);
      setResolved(document.documentElement.classList.contains('dark') ? 'dark' : 'light');
    };
    listeners.add(sync);

    // Follow the OS only while the user has not made an explicit choice.
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onSystem = () => {
      if (((localStorage.getItem(THEME_KEY) as Theme | null) ?? 'system') === 'system') {
        setResolved(apply('system'));
      }
    };
    mq.addEventListener('change', onSystem);

    return () => {
      listeners.delete(sync);
      mq.removeEventListener('change', onSystem);
    };
  }, []);

  const setTheme = useCallback((next: Theme) => {
    localStorage.setItem(THEME_KEY, next);
    apply(next);
    broadcast();
  }, []);

  /** Light ⇄ dark. Always lands on an explicit choice, never back on 'system'. */
  const toggle = useCallback(() => {
    const isDark = document.documentElement.classList.contains('dark');
    setTheme(isDark ? 'light' : 'dark');
  }, [setTheme]);

  return { theme, resolved, setTheme, toggle };
}
